/**
 * Les champs Word VIDES d'un bloc : les retirer avant d'en réécrire le texte.
 *
 * Un modèle de cabinet garde souvent des champs vidés de leur contenu : un
 * `w:fldChar begin` suivi aussitôt de son `end`, sans instruction ni résultat
 * (restes de champs de fusion). Word n'en affiche rien. L'éditeur, lui, refuse
 * de remplacer toute plage de texte qui en traverse un (`INVALID_CONTEXT` /
 * `text-range-in-field`) et de supprimer le bloc qui le porte
 * (`paragraph-tracked-wrapper-unsupported`) : le paragraphe ne peut plus être réécrit par aucun verbe (signalement du 2026-10-08, 34 champs
 * vides dans une convention de divorce, aucun paragraphe à compléter écrivable).
 *
 * ## Mesuré sur superdoc@2.15.1 (2026-10-08, document du signalement)
 *
 * - `fields.list` rend ces champs avec `instruction: ""`, `fieldType: "UNKNOWN"`,
 *   `resolvedText: ""` ; 0,3 ms pour 34 champs.
 * - `fields.remove({ target, mode: 'raw' }, { changeMode: 'direct' })` les retire,
 *   une entrée d'historique par champ, aucune révision — éditeur en `editing`
 *   comme en `suggesting`. Le remplacement du texte passe ensuite, suivi compris.
 * - ⚠️ En `changeMode: 'tracked'`, le retrait répond `success: true`, laisse une
 *   révision et le champ RESTE listé ; le remplacement échoue alors sur
 *   `text-range-in-unsupported-tracked-wrapper`. D'où le retrait toujours direct :
 *   il ne change rien de ce que l'avocat lit, il n'y a rien à lui faire relire.
 * - `occurrenceIndex` se décale à chaque retrait : la liste est relue avant chacun.
 *
 * - Après ce retrait, `blocks.delete` du bloc passe aussi, en direct comme en suivi.
 * - Un plan atomique (`mutations.apply`) dont un texte cherché traverse un champ vide JETTE
 *   `text-range-in-field` sans avancer la révision ; son aperçu ne le voit pas.
 *
 * ## Qui retire, et quand
 *
 * - `rewrite_block` (`rewriteBlockExecutor.ts`, une fois sa liste reconnue valide) et `delete_block_range` : avant d'écrire, le bloc entier étant visé.
 * - `replace_text` : seulement après le refus de l'éditeur, puis un seul nouvel essai. Un
 *   remplacement qui ne traverse aucun champ laisse le bloc tel quel.
 * - `apply_edit_plan` : ne retire rien (sa révision attendue est celle de tout le plan) ;
 *   il rend le refus et envoie vers les deux verbes ci-dessus.
 *
 * Un champ qui porte une instruction ou un résultat (date, renvoi, champ de
 * fusion rempli) n'est jamais retiré : ce serait effacer du contenu de l'avocat.
 */

import { estObjet } from './apercuDePlan';
import { muterSousFences, revisionCourante, type RevisionDeDocument } from './fencesDeMutation';
import { estUnEchecDuFournisseur } from './refusPassager';
import type { CanvasActionContext } from './textActionExecutors';

type MethodeProvider = (input?: unknown, options?: unknown) => unknown;
type DocAvecChamps = CanvasActionContext['doc'] & { fields?: { list?: MethodeProvider; remove?: MethodeProvider } };

/** Le refus de l'éditeur devant un texte qui traverse un champ : rendu (`{ success: false, failure }`) ou jeté (plan atomique). */
export const REFUS_TEXTE_DANS_UN_CHAMP = 'text-range-in-field';

/** Vrai si le fournisseur a RENDU ce refus. */
export function estUnRefusDeTexteDansUnChamp(rendu: unknown): boolean {
  return estObjet(rendu) && rendu.success === false && estObjet(rendu.failure) && rendu.failure.message === REFUS_TEXTE_DANS_UN_CHAMP;
}

/** Borne de la lecture : au-delà, les champs du bloc visé peuvent manquer et le bloc reste tel quel. */
const CHAMPS_LUS_AU_PLUS = 2000;

function premierChampVideDuBloc(liste: unknown, nodeId: string): Record<string, unknown> | undefined {
  if (!estObjet(liste) || !Array.isArray(liste.items)) return undefined;
  for (const champ of liste.items) {
    if (!estObjet(champ) || !estObjet(champ.address) || champ.address.blockId !== nodeId) continue;
    if (typeof champ.instruction !== 'string' || champ.instruction.trim() !== '') continue;
    if (typeof champ.resolvedText !== 'string' || champ.resolvedText !== '') continue;
    return champ.address;
  }
  return undefined;
}

/**
 * Retire les champs vides du bloc, en écriture directe quel que soit le régime de suivi.
 *
 * S'arrête au premier refus de l'éditeur : l'appelant écrit ensuite, et c'est le
 * refus de SON écriture qui est rendu à l'agent. Sans surface `fields`, ne fait rien.
 *
 * @param revisionAttendue Révision présentée au premier retrait (celle que
 *   l'agent a lue) ; les suivants présentent la révision relue.
 * @returns Le nombre de champs retirés : chacun a fait avancer la révision.
 */
export async function retirerLesChampsVidesDuBloc(
  ctx: CanvasActionContext,
  nodeId: string,
  revisionAttendue: RevisionDeDocument,
): Promise<number> {
  const champs = (ctx.doc as DocAvecChamps).fields;
  if (typeof champs?.list !== 'function' || typeof champs.remove !== 'function') return 0;
  let retires = 0;
  for (;;) {
    const cible = premierChampVideDuBloc(await champs.list({ limit: CHAMPS_LUS_AU_PLUS, offset: 0 }), nodeId);
    if (cible === undefined) return retires;
    const expectedRevision = retires === 0 ? revisionAttendue : await revisionCourante(ctx, revisionAttendue);
    const options = { changeMode: 'direct' as const, expectedRevision };
    const rendu = await muterSousFences(ctx, 'champ_vide:retrait', options, () => champs.remove?.({ target: cible, mode: 'raw' }, options));
    if (estUnEchecDuFournisseur(rendu)) return retires;
    retires += 1;
  }
}
